import { useAccount, useMsal } from "@azure/msal-react";
import { notifications } from "@mantine/notifications";
import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { useLocalStorage } from "@uidotdev/usehooks";
import type { IndexableType } from "dexie";
import { useLiveQuery } from "dexie-react-hooks";
import { useCallback, useEffect, useState } from "react";
import { acquireAuthToken, postAPI } from "src/utils/fetch";
import { v4 as uuidv4 } from "uuid";
import { useTab } from "../contexts/tabContext";
import {
    ChatEntity,
    ChatRole,
    db,
    MessageEntity,
    type Reference,
    type SingleSuggestionOption,
    type StreamedReference,
} from "../db";
import { useChatId } from "./useChatId";
import { useMetadata } from "./useMetadata";

interface OpenAIChatMessage {
    answer: string;
    suggestions: SingleSuggestionOption[];
    content: string;
    role: string;
}

interface ChatMessageToken extends OpenAIChatMessage {
    timestamp: number;
}

interface ChatMessageParams extends OpenAIChatMessage {
    timestamp?: number;
    meta?: {
        loading?: boolean;
        responseTime?: string;
        chunks?: ChatMessageToken[];
    };
}

export interface ChatMessage extends ChatMessageParams {
    responseId?: string;
    pretrained?: string;
    toolCall: boolean;
    timestamp: number;
    references: Reference[];
    meta: {
        loading: boolean;
        responseTime: string;
        chunks: ChatMessageToken[];
    };
}

// callback function type
type CallbackFunction = (
    answer: ChatMessage | string,
    error?: string,
    reference?: Reference,
    toolCall?: boolean,
    toolName?: string,
    toolError?: boolean,
) => void;

const makeMessagesSendingRequest = async ({
    chatId,
    content,
}: {
    chatId: string | IndexableType | undefined;
    content: string;
}): Promise<ChatMessageParams[]> => {
    if (!chatId) {
        throw new Error("Chat ID is required");
    }

    return [
        {
            content,
            role: "user",
            answer: "",
            suggestions: [],
        },
    ];
};

export const CHAT_COMPLETIONS_URL = import.meta.env.VITE_BACKEND_URL;

// Utility method for transforming a chat message that may or may not be decorated with metadata
// to a fully-fledged chat message with metadata.
const createChatMessage = ({ content, role, ...restOfParams }: ChatMessageParams): ChatMessage => ({
    content,
    answer: content,
    role,
    timestamp: restOfParams.timestamp ?? Date.now(),
    references: [],
    suggestions: [],
    toolCall: false,
    meta: {
        loading: false,
        responseTime: "",
        chunks: [],
        ...restOfParams.meta,
    },
});

const prepareUpdatedMessages = (
    messages: MessageEntity[] | undefined,
    newMessages: ChatMessageParams[] | undefined,
): ChatMessage[] => {
    const convertToChat = (msg: MessageEntity): ChatMessage => ({
        content: msg.content ?? "",
        answer: msg.content ?? "",
        role: msg.role,
        timestamp: msg.createdAt ?? Date.now(),
        references: msg.references ?? [],
        suggestions: msg.suggestions ?? [],
        toolCall: msg.toolCall,
        meta: {
            loading: false,
            responseTime: "",
            chunks: [],
        },
    });

    return [
        ...(messages?.map(convertToChat) ?? []),
        ...(newMessages?.map(createChatMessage) ?? []),
        createChatMessage({
            content: "",
            role: "",
            answer: "",
            suggestions: [],
            meta: { loading: true },
        }),
    ];
};

interface AnswerJson {
    content: string;
    responseId?: string;
    response_id?: string;
    pretrained?: string;
    suggestions?: SingleSuggestionOption[];
    references?: Reference[];
    tool_call: boolean;
}

export const preparePayload = (
    newMessages: ChatMessageParams[] | undefined,
    chatId: string | undefined,
    metadata: Record<string, string>[] | undefined,
    datasources?: string[],
) => {
    if (!chatId) return undefined;
    const question = newMessages?.reverse().find((m) => m.role === "user")?.content ?? "";

    return JSON.stringify({
        question,
        conversation_id: chatId,
        options: {
            debug: false,
            metadata_filter: metadata ?? [],
            ...(datasources && datasources.length > 0 ? { datasources } : {}),
        },
    }).replace('"id":"', '"key":"');
};

const isWidgetMode = (): boolean => {
    return (
        window.location.hash.includes("/copilot") ||
        window.location.search.includes("mode=copilot") ||
        window.parent !== window
    );
};

const getHostApp = (): string => {
    try {
        if (document.referrer) {
            const url = new URL(document.referrer);
            // Special handling for Omnivise apps - extract app name from path
            // e.g., /auth/v/solview → omnivise/solview
            if (url.hostname.includes("omnivise")) {
                const pathMatch = url.pathname.match(/\/auth\/v\/([^/]+)/);
                if (pathMatch) {
                    return `omnivise/${pathMatch[1]}`;
                }
                return "omnivise";
            }
            return url.hostname;
        }
    } catch {
        // Ignore URL parsing errors
    }
    return "unknown";
};

const makeApiRequest = async (
    CHAT_ENDPOINT_STREAM: string,
    payload: string | undefined,
    getTokenSilently: (forceRefresh?: boolean) => Promise<string | null>,
    conversationId?: string,
) => {
    if (!payload) {
        throw new Error("No payload provided");
    }

    const abortController = new AbortController();
    if (conversationId) {
        abortedConversations.delete(conversationId);
        abortControllers.set(conversationId, abortController);
    }

    const transactionId = uuidv4();
    Sentry.withScope((scope) => {
        scope.setTag("transaction_id", transactionId);
    });
    const execute = async (forceRefresh = false): Promise<Response> => {
        const freshToken = await getTokenSilently(forceRefresh);
        if (!freshToken) {
            throw new Error("Unauthorized");
        }

        const isWidget = isWidgetMode();
        const CHAT_HEADERS: Record<string, string> = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${freshToken}`,
            "X-Transaction-ID": transactionId,
            Accept: "text/event-stream",
            ...(isWidget && {
                "X-CTS-Widget": "true",
                "X-CTS-Widget-App": getHostApp(),
            }),
        };

        const response = await fetch(CHAT_ENDPOINT_STREAM, {
            headers: CHAT_HEADERS,
            method: "POST",
            cache: "no-cache",
            keepalive: true,
            body: payload,
            signal: abortController.signal,
        });

        if (response.status === 401 && !forceRefresh) {
            return execute(true);
        }

        if (response.status === 401) {
            throw new Error("Unauthorized");
        }

        return response;
    };

    return execute();
};
const processResponse = async (
    response: Response,
    isStreamEnabled: boolean,
    callback: CallbackFunction,
    conversationId?: string,
) => {
    let answer = "";
    let answerJson: AnswerJson = {
        content: "",
        tool_call: false,
    };

    if (isStreamEnabled) {
        ({ answer, answerJson } = await processStreamResponse(response, callback, conversationId));
    } else {
        ({ answer, answerJson } = await processNonStreamResponse(response));
    }

    if (!answer) {
        throw new Error("No response from API");
    }
    return { answer, answerJson };
};

const processStreamResponse = async (
    response: Response,
    callback: CallbackFunction,
    conversationId?: string,
) => {
    let answer = "";
    let toolInterrupted = false;
    const answerJson: AnswerJson = {
        content: "",
        tool_call: false,
    };

    // Check for error status codes first
    if (response.status === 403) {
        const errorData = await response.json();
        const errorMessage =
            (Array.isArray(errorData.detail) ? errorData.detail[0]?.msg : errorData.detail) ||
            "You are not authorized to access this resource. Please contact the administrator.";
        callback("", errorMessage);
        throw new Error(errorMessage);
    }

    if (!response.ok) {
        const errorData = await response.json();
        const errorMessage =
            (Array.isArray(errorData.detail) ? errorData.detail[0]?.msg : errorData.detail) ||
            "An error occurred while processing your request";
        callback("", errorMessage);
        throw new Error(errorMessage);
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = ""; // Buffer for incomplete SSE lines across chunks
    setStreamingState(true, conversationId);

    // Stop draining/rendering the moment the user hits Stop, even if a chunk with many
    // buffered SSE lines has already been read into memory.
    const abortRequested = () =>
        Boolean(conversationId) && abortedConversations.has(conversationId as string);
    const raiseAbort = () => {
        const abortError = new Error("Stream aborted by user");
        abortError.name = "AbortError";
        throw abortError;
    };

    try {
        while (reader) {
            if (abortRequested()) raiseAbort();

            const { value, done } = await reader.read();
            if (done) break;
            if (abortRequested()) raiseAbort();

            // Append new data to buffer and split by newlines
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");

            // Keep the last incomplete line in the buffer (if any)
            buffer = lines.pop() ?? "";

            for (const line of lines) {
                if (abortRequested()) raiseAbort();
                if (line.startsWith("data: ")) {
                    const jsonValue = parseJsonLine(line.slice(6));

                    // Check for end event and reset streaming state immediately
                    if (jsonValue?.event === "end") {
                        setStreamingState(false, conversationId);
                    }

                    if (jsonValue?.event === "error") {
                        const errorMessage = jsonValue.content || "An error occurred";
                        // Check if the error message indicates an authorization issue
                        if (
                            errorMessage.toLowerCase().includes("not authorized") ||
                            errorMessage.toLowerCase().includes("unauthorized") ||
                            errorMessage.includes("403")
                        ) {
                            const fullError = `${errorMessage}. Please contact the administrator.`;
                            callback("", fullError);
                            throw new Error(fullError);
                        }
                        callback("", errorMessage);
                        throw new Error(errorMessage);
                    }
                    const updatedAnswer = updateAnswerAndJson(
                        jsonValue,
                        answerJson,
                        callback,
                        conversationId,
                    );
                    if (updatedAnswer) {
                        if (toolInterrupted) {
                            if (answer) answer += " \n\n ⤷ ";
                            toolInterrupted = false;
                        }
                        answer += updatedAnswer;
                        callback?.(answer);
                    } else if (jsonValue?.event === "on_tool_end") {
                        toolInterrupted = true;
                    }
                }
            }
        }
    } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
            const interruptedMessage = answer
                ? `${answer}\n\n_[Response interrupted by user]_`
                : "_Response interrupted by user_";
            callback(interruptedMessage);
            return { answer: interruptedMessage, answerJson };
        }
        const errorMessage =
            error instanceof Error
                ? error.message
                : "An error occurred while streaming the response";
        callback("", errorMessage);
        throw error;
    } finally {
        setStreamingState(false, conversationId);
    }

    return { answer, answerJson };
};

const processNonStreamResponse = async (response: Response) => {
    const answerJson = await response.json();
    if (response.status === 403) {
        throw new Error(
            "You are not authorized to access this resource. Please contact the administrator.",
        );
    }
    if (response.status !== 200) {
        const errorMessage = Array.isArray(answerJson.detail)
            ? answerJson.detail[0]?.msg
            : answerJson.detail;
        throw new Error(errorMessage || "API response was not OK");
    }
    const answer = answerJson?.content;
    return { answer, answerJson };
};

const parseJsonLine = (jsonString: string) => {
    try {
        return JSON.parse(jsonString);
    } catch (error) {
        console.error("Error parsing JSON:", error);
        return null;
    }
};

/**
 * Parses reference/tool content from the SSE stream into a JavaScript object.
 * Accepts real JSON first, then falls back to Python-style dict repr
 * (single quotes, True/False/None).
 * Returns null if parsing fails, allowing callers to handle gracefully.
 */
export const parseContent = (content: string): unknown => {
    if (!content?.trim()) {
        return null;
    }

    try {
        return JSON.parse(content);
    } catch {
        // Fall through to Python-repr sanitization.
    }

    try {
        // Protect apostrophes that occur within words (such as contractions or possessives)
        const protectedContent = content.replace(/(\w)'(\w)/g, "$1<APOS>$2");

        // Replace remaining apostrophes (used as delimiters) with double quotes
        const replaced = protectedContent.replace(/'/g, '"');

        // Restore the protected apostrophes back to literal apostrophes
        const restored = replaced.replace(/<APOS>/g, "'");

        // Fix boolean and null literals to conform to JSON
        const sanitized = restored
            .replace(/:\s*True/g, ": true")
            .replace(/:\s*False/g, ": false")
            .replace(/:\s*None/g, ": null");

        return JSON.parse(sanitized);
    } catch {
        return null;
    }
};

const resolveToolName = (content: string | undefined, metadata?: { title?: string }): string => {
    if (typeof metadata?.title === "string") return metadata.title;
    return content ?? "";
};

const updateAnswerAndJson = (
    jsonValue: unknown,
    answerJson: AnswerJson,
    callback: CallbackFunction,
    conversationId?: string,
) => {
    if (!jsonValue || typeof jsonValue !== "object") return;

    const jsonObj = jsonValue as {
        event?: string;
        content?: string;
        metadata?: { title?: string; plotly_figure?: Reference["plotlyFigure"] };
    };

    if (jsonObj.event === "error") {
        return callback("", jsonObj.content);
    }

    // Handle end event to ensure loading state is properly reset
    if (jsonObj.event === "end") {
        setStreamingState(false, conversationId);
        return;
    }

    if (jsonObj.event === "response_id") {
        answerJson.response_id = jsonObj.content;
    }

    if (jsonObj.event === "message") {
        return jsonObj.content;
    }

    if (jsonObj.event === "on_tool_start") {
        answerJson.tool_call = true;
        callback(
            "",
            undefined,
            undefined,
            true,
            resolveToolName(jsonObj.content, jsonObj.metadata),
        );
    }

    if (jsonObj.event === "on_tool_end") {
        answerJson.tool_call = false;
        callback(
            "",
            undefined,
            undefined,
            false,
            resolveToolName(jsonObj.content, jsonObj.metadata),
        );
    }

    if (jsonObj.event === "on_tool_error") {
        answerJson.tool_call = false;
        callback(
            "",
            undefined,
            undefined,
            false,
            resolveToolName(jsonObj.content, jsonObj.metadata),
            true,
        );
    }

    if (jsonObj.event === "reference") {
        const parsedReference = parseContent(jsonObj.content ?? "") as StreamedReference | null;
        if (!parsedReference) return; // Skip malformed reference data

        if (!answerJson.references) {
            answerJson.references = [];
        }
        const newReference: Reference = {
            reference_id: parsedReference.reference_id,
            title: parsedReference.title,
            source: (() => {
                try {
                    return decodeURIComponent(parsedReference.source ?? "");
                } catch {
                    return parsedReference.source ?? "";
                }
            })(),
            dataSourceType: parsedReference.data_source_type,
            page_numbers: new Set([parsedReference.page_number]),
            plotlyFigure:
                jsonObj.metadata?.plotly_figure ?? parsedReference.plotly_figure ?? undefined,
            displayMode: parsedReference.display_mode,
            metric: parsedReference.metric,
        };
        if (newReference.source !== "") {
            const existingReference = answerJson.references.find(
                (ref: Reference) => ref.source === newReference.source,
            );

            if (existingReference) {
                // Merge page_numbers sets
                for (const page of newReference.page_numbers) {
                    existingReference.page_numbers.add(page);
                }
                if (!existingReference.plotlyFigure && newReference.plotlyFigure) {
                    existingReference.plotlyFigure = newReference.plotlyFigure;
                }
                if (!existingReference.metric && newReference.metric) {
                    existingReference.metric = newReference.metric;
                }
                if (!existingReference.displayMode && newReference.displayMode) {
                    existingReference.displayMode = newReference.displayMode;
                }
            } else {
                answerJson.references.push(newReference);
                callback("", undefined, newReference);
            }
        }
    }
};

const prepareReturnValue = (
    answer: string,
    answerJson: AnswerJson,
    updatedMessages: ChatMessage[],
    beforeTimestamp: number,
    afterTimestamp: number,
): ChatMessage => {
    const diffInSeconds = (afterTimestamp - beforeTimestamp) / 1000;
    const formattedDiff = `${diffInSeconds.toFixed(2)} sec.`;
    return {
        answer,
        responseId: answerJson?.response_id,
        content: answer,
        pretrained: answerJson?.pretrained,
        role: "user",
        timestamp: afterTimestamp,
        references: answerJson?.references ?? [],
        suggestions: [],
        toolCall: answerJson.tool_call,
        meta: {
            ...updatedMessages[updatedMessages.length - 1].meta,
            loading: false,
            responseTime: formattedDiff,
        },
    };
};

const handleError = (
    error: unknown,
    setIsloading: (value: boolean) => void,
    setAcquireToken: (value: boolean) => void,
    callback?: CallbackFunction,
    conversationId?: string,
) => {
    let errorMessage = "An error occurred";
    if (typeof error === "string") {
        errorMessage = error;
    } else if (error instanceof Error) {
        errorMessage = error.message;
    } else if (typeof error === "object" && error !== null && "message" in error) {
        errorMessage = (error as { message?: string }).message ?? errorMessage;
    }
    setIsloading(false);
    setStreamingState(false, conversationId);
    callback?.(errorMessage, errorMessage);
    if (errorMessage === "Unauthorized") {
        setAcquireToken(true);
    }
};

const streamingEmitter = new EventTarget();

// Streaming state is tracked per conversation so that a generation running in one chat
// never blocks input in another chat. Each entry means "this conversation has an in-flight
// stream". `abortControllers` holds one controller per streaming conversation so Stop only
// affects that chat; `abortedConversations` records which conversations the user stopped
// (used to suppress the sidebar badge and follow-up suggestions for that run).
const streamingStates = new Map<string, boolean>();
const abortControllers = new Map<string, AbortController>();
const abortedConversations = new Set<string>();

// Outcome of a background generation that finished while the user was viewing another chat.
// Deliberately in-memory only (no localStorage / Dexie): a page refresh clears every badge.
const backgroundStatuses = new Map<string, "ready" | "failed">();

interface StreamingChangeDetail {
    conversationId?: string;
    isStreaming: boolean;
    /** When true, every conversation's state was reset (module load / stale cleanup). */
    all?: boolean;
}

// Track streaming state by conversation ID
const getStreamingStateKey = () => "chatStreamingStates";

export const setStreamingState = (isStreaming: boolean, conversationId?: string) => {
    if (!conversationId) {
        // No conversation scope: `false` is a global reset (module load / legacy callers);
        // `true` is meaningless without a target, so ignore it.
        if (!isStreaming) resetAllStreamingStates();
        return;
    }

    if (isStreaming) {
        streamingStates.set(conversationId, true);
    } else {
        streamingStates.delete(conversationId);
    }

    streamingEmitter.dispatchEvent(
        new CustomEvent<StreamingChangeDetail>("streamingChange", {
            detail: { conversationId, isStreaming },
        }),
    );

    // Mirror into localStorage so the state survives remounts / route changes within a session.
    if (typeof window !== "undefined") {
        try {
            const storedStates = localStorage.getItem(getStreamingStateKey());
            const persisted = storedStates ? JSON.parse(storedStates) : {};

            if (isStreaming) {
                persisted[conversationId] = true;
            } else {
                delete persisted[conversationId];
            }

            localStorage.setItem(getStreamingStateKey(), JSON.stringify(persisted));
        } catch (error) {
            console.error("Error updating streaming state:", error);
        }
    }
};

const getStreamingState = (conversationId?: string): boolean => {
    // A brand-new chat with no id yet is never streaming.
    if (!conversationId) return false;

    if (streamingStates.get(conversationId)) return true;

    if (typeof window !== "undefined") {
        try {
            const storedStates = localStorage.getItem(getStreamingStateKey());
            if (storedStates) {
                return !!JSON.parse(storedStates)[conversationId];
            }
        } catch (error) {
            console.error("Error retrieving streaming state:", error);
        }
    }

    return false;
};

// Clean up any stale streaming states on load
const resetAllStreamingStates = () => {
    streamingStates.clear();
    if (typeof window !== "undefined") {
        try {
            localStorage.setItem(getStreamingStateKey(), JSON.stringify({}));
        } catch (error) {
            console.error("Error resetting streaming states:", error);
        }
    }
    streamingEmitter.dispatchEvent(
        new CustomEvent<StreamingChangeDetail>("streamingChange", {
            detail: { isStreaming: false, all: true },
        }),
    );
};

// Initialize: reset stale states on module load (in-flight fetches don't survive a reload)
if (typeof window !== "undefined") {
    // Only reset in browser context
    resetAllStreamingStates();
}

/** Abort the in-flight stream for a single conversation (Stop button). */
export const abortStream = (conversationId?: string) => {
    if (!conversationId) return;
    const controller = abortControllers.get(conversationId);
    if (!controller) return;
    abortedConversations.add(conversationId);
    controller.abort();
    abortControllers.delete(conversationId);
    setStreamingState(false, conversationId);
};

export const useApiStreamingState = (conversationId?: string) => {
    const [isStreaming, setIsStreaming] = useState(() => getStreamingState(conversationId));

    useEffect(() => {
        // Re-sync when the viewed conversation changes.
        setIsStreaming(getStreamingState(conversationId));

        const handleStreamingChange = (event: Event) => {
            const { detail } = event as CustomEvent<StreamingChangeDetail>;
            if (detail?.all) {
                setIsStreaming(getStreamingState(conversationId));
                return;
            }
            if (conversationId && detail?.conversationId === conversationId) {
                setIsStreaming(Boolean(detail.isStreaming));
            }
        };

        streamingEmitter.addEventListener("streamingChange", handleStreamingChange);
        return () => {
            streamingEmitter.removeEventListener("streamingChange", handleStreamingChange);
        };
    }, [conversationId]);

    return isStreaming;
};

/** Record the outcome of a background generation for the sidebar badge (session-only). */
export const setBackgroundStatus = (conversationId: string, status: "ready" | "failed") => {
    backgroundStatuses.set(conversationId, status);
    streamingEmitter.dispatchEvent(
        new CustomEvent("backgroundStatusChange", { detail: { conversationId } }),
    );
};

/** Drop the badge for a conversation (called when the user opens it). */
export const clearBackgroundStatus = (conversationId: string) => {
    if (backgroundStatuses.delete(conversationId)) {
        streamingEmitter.dispatchEvent(
            new CustomEvent("backgroundStatusChange", { detail: { conversationId } }),
        );
    }
};

export type ChatBadgeStatus = "running" | "ready" | "failed" | undefined;

/**
 * Sidebar badge state for a chat: live "running" while it streams, otherwise the last
 * background outcome ("ready" / "failed") until the chat is opened. Not persisted.
 */
export const useChatBackgroundBadge = (conversationId?: string): ChatBadgeStatus => {
    const isStreaming = useApiStreamingState(conversationId);
    const [status, setStatus] = useState<"ready" | "failed" | undefined>(() =>
        conversationId ? backgroundStatuses.get(conversationId) : undefined,
    );

    useEffect(() => {
        setStatus(conversationId ? backgroundStatuses.get(conversationId) : undefined);

        const handler = (event: Event) => {
            const { detail } = event as CustomEvent<{ conversationId?: string }>;
            if (conversationId && detail?.conversationId === conversationId) {
                setStatus(backgroundStatuses.get(conversationId));
            }
        };

        streamingEmitter.addEventListener("backgroundStatusChange", handler);
        return () => streamingEmitter.removeEventListener("backgroundStatusChange", handler);
    }, [conversationId]);

    return isStreaming ? "running" : status;
};

// In-flight assistant answer text, keyed by message id. Populated while a response streams
// and rendered live by MessageItem; IndexedDB is written only once, when the stream ends.
const liveMessageContent = new Map<string, string>();

export const setLiveMessageContent = (messageId: string, content: string) => {
    liveMessageContent.set(messageId, content);
    streamingEmitter.dispatchEvent(new CustomEvent("liveContentChange", { detail: { messageId } }));
};

export const clearLiveMessageContent = (messageId: string) => {
    if (liveMessageContent.delete(messageId)) {
        streamingEmitter.dispatchEvent(
            new CustomEvent("liveContentChange", { detail: { messageId } }),
        );
    }
};

/**
 * Live streamed text for one assistant message. Returns the in-memory text only while it is
 * ahead of what has been persisted; once IndexedDB has caught up (the single end-of-stream
 * write) it returns `undefined` and the caller falls back to `persistedContent`. Cleanup is
 * completion-driven, not time-driven.
 */
export const useLiveStreamedContent = (
    messageId: string | undefined,
    persistedContent: string,
): string | undefined => {
    const [live, setLive] = useState<string | undefined>(() =>
        messageId ? liveMessageContent.get(messageId) : undefined,
    );

    useEffect(() => {
        setLive(messageId ? liveMessageContent.get(messageId) : undefined);

        const handler = (event: Event) => {
            const { detail } = event as CustomEvent<{ messageId?: string }>;
            if (messageId && detail?.messageId === messageId) {
                setLive(liveMessageContent.get(messageId));
            }
        };

        streamingEmitter.addEventListener("liveContentChange", handler);
        return () => streamingEmitter.removeEventListener("liveContentChange", handler);
    }, [messageId]);

    const caughtUp = live == null || (!!persistedContent && persistedContent.length >= live.length);

    useEffect(() => {
        if (messageId && caughtUp && liveMessageContent.has(messageId)) {
            clearLiveMessageContent(messageId);
        }
    }, [messageId, caughtUp]);

    return caughtUp ? undefined : live;
};

export const useChatCompletion = (_chatId?: IndexableType, isChatWithDocument?: boolean) => {
    const { instance } = useMsal();
    const account = useAccount();
    const chatId = useChatId() || _chatId;
    const { selectedTab } = useTab();
    const [_, setAcquireToken] = useLocalStorage("acquireToken", false);
    const [isloading, setIsloading] = useState(false);
    const isStreamEnabled = true;

    const messages = useLiveQuery(() => {
        if (!chatId) return [];
        return db.messages.where("chatId").equals(chatId).sortBy("createdAt");
    }, [chatId]);

    // Reset the local loading flag whenever chatId changes (new chat or navigation between
    // chats). Per-conversation streaming state (useApiStreamingState) is the durable signal
    // for whether a given chat is generating, so this must not touch other chats' state.
    // biome-ignore lint/correctness/useExhaustiveDependencies: re-run on chat switch to clear stale loading
    useEffect(() => {
        setIsloading(false);
    }, [chatId]);

    // Function to continue or start a new conversation
    const continueConversation = useCallback(
        async (existingChatId?: IndexableType) => {
            if (existingChatId) {
                return existingChatId;
            }

            const tabToUse = isChatWithDocument ? selectedTab : "combined";

            // Create a new chat and persist its ID
            const newChatId = await ChatEntity._(tabToUse).add();
            return newChatId;
        },
        [selectedTab, isChatWithDocument],
    );

    const getTokenSilently = useCallback(
        async (forceRefresh = false) => {
            if (!account) {
                return null;
            }
            try {
                return await acquireAuthToken(instance, { forceRefresh });
            } catch (_error) {
                return null;
            }
        },
        [account, instance],
    );

    const send = useCallback(
        async (
            newMessages: ChatMessageParams[],
            callback: CallbackFunction,
            metadata?: Record<string, string>[],
            messageChatId?: string | IndexableType,
            datasources?: string[],
        ) => {
            if (messages && messages[messages.length - 1]?.done === false) return;

            const beforeTimestamp = Date.now();

            const updatedMessages = prepareUpdatedMessages(messages, newMessages);

            const conversationId = chatId?.toString() ?? messageChatId?.toString();

            const payload = preparePayload(newMessages, conversationId, metadata, datasources);

            try {
                setIsloading(true);
                // Set streaming state with conversation ID at the start of stream
                setStreamingState(true, conversationId);

                const CHAT_ENDPOINT_STREAM = `${CHAT_COMPLETIONS_URL}/v1/chat/stream`;

                const response = await makeApiRequest(
                    CHAT_ENDPOINT_STREAM,
                    payload,
                    getTokenSilently,
                    conversationId,
                );
                const { answer, answerJson } = await processResponse(
                    response,
                    isStreamEnabled,
                    callback,
                    conversationId,
                );

                const afterTimestamp = Date.now();
                const returnValue = prepareReturnValue(
                    answer,
                    answerJson,
                    updatedMessages,
                    beforeTimestamp,
                    afterTimestamp,
                );

                callback?.(returnValue);
                setIsloading(false);
                // Reset streaming state with conversation ID at the end of stream
                setStreamingState(false, conversationId);
            } catch (error) {
                setStreamingState(false, conversationId);
                if (error instanceof Error && error.name === "AbortError") {
                    setIsloading(false);
                    return;
                }
                handleError(error, setIsloading, setAcquireToken, callback, conversationId);
            } finally {
                if (conversationId) abortControllers.delete(conversationId);
            }
        },
        [
            chatId,
            messages,
            setAcquireToken,
            getTokenSilently, // Add getTokenSilently to dependencies
        ],
    );

    const navigate = useNavigate();

    const { chatMetadata } = useMetadata();

    const sendMessage = useCallback(
        async (
            messageContent: string,
            chatId?: IndexableType,
            metadataFilter?: { key: string; value: string }[],
            onChatCreated?: (chatId: IndexableType) => void,
            datasources?: string[],
        ): Promise<IndexableType> => {
            let messageChatId: IndexableType;
            let errorHappened = false;
            let isNewChat = false;

            // Streamed text deltas are NOT written to IndexedDB token-by-token — that makes
            // the DB (and the live-query re-render) fall behind a fast stream, so the answer
            // keeps "typing" long after the network stream finished. Instead the in-flight
            // text is published to an in-memory store (throttled) and rendered live by
            // MessageItem; IndexedDB is written exactly once, when the response completes.
            const STREAM_PUBLISH_MS = 60;
            let assistantMessageId: IndexableType | undefined;
            let pendingContent: string | null = null;
            let latestContent: string | null = null;
            let finalPersisted = false;
            let publishTimer: ReturnType<typeof setTimeout> | null = null;
            const publishPendingContent = () => {
                if (pendingContent === null || assistantMessageId === undefined) return;
                latestContent = pendingContent;
                pendingContent = null;
                setLiveMessageContent(String(assistantMessageId), latestContent);
            };
            const cancelPublishTimer = () => {
                if (publishTimer) {
                    clearTimeout(publishTimer);
                    publishTimer = null;
                }
            };

            if (!chatId) {
                // If no chat ID provided, create a new chat
                messageChatId = await continueConversation();
                isNewChat = true;
                // Check if we're in copilot mode - if so, don't navigate away
                const isCopilotMode =
                    window.location.hash.includes("/copilot") ||
                    window.location.search.includes("mode=copilot");
                if (!isCopilotMode) {
                    navigate({ to: `/chats/${messageChatId}`, replace: true });
                }
            } else {
                messageChatId = chatId;
            }

            try {
                setIsloading(true);
                const messagesSending = await makeMessagesSendingRequest({
                    chatId: messageChatId.toString(),
                    content: messageContent,
                });
                const userMessageId = await MessageEntity._()
                    .setChatId(messageChatId)
                    .setRole(ChatRole.USER)
                    .setContent(messageContent)
                    .add();

                const assistantMessageReceivedId = await MessageEntity._()
                    .setChatId(messageChatId)
                    .setRole(ChatRole.ASSISTANT)
                    .setContent("")
                    .setRepliedId(userMessageId as string)
                    .setDatasources(datasources ?? [])
                    .add();

                assistantMessageId = assistantMessageReceivedId;

                // Notify caller that chat was created (before streaming starts)
                // This allows UI to update and show messages immediately
                if (isNewChat && onChatCreated) {
                    onChatCreated(messageChatId);
                }

                await send(
                    messagesSending,
                    (async (
                        chatMessage: ChatMessage | string,
                        error?: string,
                        reference?: Reference,
                        toolCall?: boolean,
                        toolName?: string,
                        toolError?: boolean,
                    ) => {
                        if (toolCall !== undefined) {
                            const currentMsg = await db.messages.get(
                                assistantMessageReceivedId as string,
                            );
                            if (currentMsg) {
                                if (toolCall) {
                                    const activities = [...(currentMsg.toolActivities ?? [])];
                                    const reuseIdx = activities.findIndex(
                                        (a) => a.internalName === (toolName ?? ""),
                                    );
                                    if (reuseIdx !== -1) {
                                        activities[reuseIdx] = {
                                            internalName: toolName ?? "",
                                            done: false,
                                        };
                                    } else {
                                        activities.push({
                                            internalName: toolName ?? "",
                                            done: false,
                                        });
                                    }
                                    await db.messages.update(assistantMessageReceivedId, {
                                        toolCall: true,
                                        toolCallCount: (currentMsg.toolCallCount ?? 0) + 1,
                                        toolActivities: activities,
                                    });
                                } else {
                                    const activities = [...(currentMsg.toolActivities ?? [])];
                                    const idx = activities.findIndex(
                                        (a) =>
                                            a.internalName === (toolName ?? "") &&
                                            !a.done &&
                                            !a.error,
                                    );
                                    if (idx !== -1)
                                        activities[idx] = {
                                            ...activities[idx],
                                            done: true,
                                            error: toolError ?? false,
                                        };
                                    const newCount = Math.max(
                                        0,
                                        (currentMsg.toolCallCount ?? 0) - 1,
                                    );
                                    await db.messages.update(assistantMessageReceivedId, {
                                        toolCall: newCount > 0,
                                        toolCallCount: newCount,
                                        toolActivities: activities,
                                    });
                                }
                            }
                            if (error) errorHappened = true;
                        } else if (typeof chatMessage === "string" && !error && !reference) {
                            // Plain streamed text delta — publish to the in-memory store on a
                            // short interval; nothing touches IndexedDB until the response ends.
                            pendingContent = chatMessage;
                            if (!publishTimer) {
                                publishTimer = setTimeout(() => {
                                    publishTimer = null;
                                    publishPendingContent();
                                }, STREAM_PUBLISH_MS);
                            }
                        } else {
                            // A reference (mid-stream) OR an error / the final message object.
                            if (!reference) {
                                // End-of-stream content: this is the single IndexedDB write.
                                cancelPublishTimer();
                                pendingContent = null;
                            }
                            await db.messages
                                .where({ id: assistantMessageReceivedId })
                                .modify((message) => {
                                    if (reference) {
                                        const already = message.references?.some(
                                            (r) =>
                                                r.reference_id === reference.reference_id ||
                                                (r.source &&
                                                    reference.source &&
                                                    r.source === reference.source),
                                        );
                                        if (!already) {
                                            message.references.push(reference);
                                        } else {
                                            const existing = message.references.find(
                                                (r) =>
                                                    r.reference_id === reference.reference_id ||
                                                    (r.source &&
                                                        reference.source &&
                                                        r.source === reference.source),
                                            );
                                            if (
                                                existing &&
                                                !existing.plotlyFigure &&
                                                reference.plotlyFigure
                                            ) {
                                                existing.plotlyFigure = reference.plotlyFigure;
                                            }
                                            if (existing && !existing.metric && reference.metric) {
                                                existing.metric = reference.metric;
                                            }
                                            if (
                                                existing &&
                                                !existing.displayMode &&
                                                reference.displayMode
                                            ) {
                                                existing.displayMode = reference.displayMode;
                                            }
                                        }
                                    } else if (typeof chatMessage === "string")
                                        message.content = chatMessage;
                                    else {
                                        message.content = chatMessage.content;
                                        message.responseId = chatMessage.responseId || "";
                                        message.pretrained = chatMessage.pretrained || "";
                                        message.suggestions = chatMessage.suggestions || [];
                                        message.toolCall = chatMessage.toolCall;
                                        if (chatMessage.references?.length) {
                                            message.references = chatMessage.references;
                                        }
                                    }
                                    message.hasError = Boolean(error);
                                    message.error = error || "";
                                    if (message.hasError) errorHappened = true;
                                });
                            if (!reference) {
                                // Content was just persisted — keep the live store in sync so
                                // there's no flicker, and mark the final write as done.
                                const persisted =
                                    typeof chatMessage === "string"
                                        ? chatMessage
                                        : chatMessage.content;
                                latestContent = persisted;
                                finalPersisted = true;
                                setLiveMessageContent(
                                    String(assistantMessageReceivedId),
                                    persisted,
                                );
                            }
                        }
                    }) as CallbackFunction,
                    metadataFilter || chatMetadata,
                    messageChatId,
                    datasources,
                );
            } catch (error: unknown) {
                errorHappened = true;
                const errMsg =
                    typeof error === "string"
                        ? error
                        : typeof error === "object" && error !== null && "message" in error
                          ? ((error as { message?: string }).message ?? "Error Happened")
                          : "Error Happened";
                notifications.show({
                    title: "Error",
                    color: "red",
                    message: errMsg,
                });
            } finally {
                cancelPublishTimer();
                // Guarantee the single end-of-stream IndexedDB write even on paths where no
                // final message object arrived (e.g. an aborted fetch that returns early).
                // The in-memory copy is cleared by useLiveStreamedContent once the persisted
                // value catches up — completion-driven, no timers.
                if (assistantMessageId !== undefined && !finalPersisted) {
                    const finalText = pendingContent ?? latestContent;
                    if (finalText !== null) {
                        await db.messages.update(assistantMessageId, { content: finalText });
                        setLiveMessageContent(String(assistantMessageId), finalText);
                    }
                }

                const chatIdStr = messageChatId.toString();
                const wasAborted = abortedConversations.has(chatIdStr);
                abortedConversations.delete(chatIdStr);

                // Raise a session-only sidebar badge when the generation finished while the
                // user was looking at a different chat. A user-initiated Stop does not.
                const viewingThisChat = window.location.hash.includes(`/chats/${chatIdStr}`);
                if (!wasAborted && !viewingThisChat) {
                    setBackgroundStatus(chatIdStr, errorHappened ? "failed" : "ready");
                }

                setTimeout(async () => {
                    // Wait 1000 ms until conversation is created in OpenSearch.
                    // Follow-ups are best-effort: API failures must stay silent (no toast / no UI).
                    let suggestions: string[] = [];
                    if (!errorHappened && !wasAborted) {
                        try {
                            const suggestedMessages = await (
                                await postAPI({
                                    url: `${CHAT_COMPLETIONS_URL}/v1/conversations/${messageChatId}/follow_up`,
                                    instance,
                                })
                            ).json();
                            suggestions = suggestedMessages.suggestions ?? [];
                        } catch {
                            suggestions = [];
                        }
                    }
                    db.chats.update(messageChatId, {
                        updatedAt: new Date(),
                        suggestedMessages: suggestions,
                        totalMessages: await db.messages.where({ chatId: messageChatId }).count(),
                    });
                }, 1000);
            }
            return messageChatId;
        },
        [continueConversation, navigate, send, chatMetadata, instance],
    );

    return {
        isloading,
        setIsloading,
        send,
        sendMessage,
    };
};
